import React, { useEffect, useState } from 'react';
import { Link, useLocation, Outlet, useNavigate } from 'react-router-dom';
import {
    LayoutDashboard,
    Users,
    Store,
    ShoppingCart,
    FileText,
    BarChart3,
    Settings,
    Bell,
    LogOut,
    Menu,
    X,
    ChevronDown,
    User,
    ChevronsLeft,
    ChevronsRight,
} from 'lucide-react';
import { toast, Toaster } from 'react-hot-toast';
import { useAuth, useAuthStore } from '@/store/authStore';

const STORAGE_KEY = 'admin.sidebarCollapsed';

const AdminLayout: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { logout } = useAuthStore();

    const [sidebarOpen, setSidebarOpen] = useState(false); // móvil
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false); // desktop
    const [userMenuOpen, setUserMenuOpen] = useState(false);

    useEffect(() => {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) setSidebarCollapsed(saved === '1');
    }, []);

    const toggleSidebarCollapsed = () => {
        setSidebarCollapsed((prev) => {
            const next = !prev;
            localStorage.setItem(STORAGE_KEY, next ? '1' : '0');
            return next;
        });
    };

    const handleLogout = () => {
        logout();
        navigate('/');
        toast.success('Sesión cerrada correctamente');
    };

    const sidebarItems = [
        { name: 'Dashboard', href: '/admin/dashboard', icon: LayoutDashboard, badge: null },
        { name: 'Solicitudes de Vendedores', href: '/admin/seller-requests', icon: Store, badge: '3' },
        { name: 'Gestión de Usuarios', href: '/admin/users', icon: Users, badge: null },
        { name: 'Órdenes', href: '/admin/orders', icon: ShoppingCart, badge: null },
        { name: 'Reseñas', href: '/admin/reviews', icon: FileText, badge: '5' },
        { name: 'Analíticas', href: '/admin/analytics', icon: BarChart3, badge: null },
        { name: 'Configuración', href: '/admin/settings', icon: Settings, badge: null },
    ];

    const isActive = (path: string) => location.pathname === path;

    return (
        <div className="flex h-screen bg-gray-100">
            {/* Sidebar Desktop */}
            <div className="hidden lg:flex lg:flex-shrink-0">
                <div className={`flex flex-col ${sidebarCollapsed ? 'w-16' : 'w-64'} transition-all duration-200`}>
                    <div className="flex flex-col flex-grow bg-white border-r border-gray-200 pt-5 pb-4 overflow-y-auto">
                        {/* Logo */}
                        <div className={`flex items-center px-4 ${sidebarCollapsed ? 'justify-center' : 'justify-start'}`}>
                            <Link to="/" className="flex items-center space-x-2">
                                <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center">
                                    <span className="text-white font-bold text-lg">T</span>
                                </div>
                                {!sidebarCollapsed && (
                                    <div>
                                        <span className="text-xl font-bold text-gray-900">Eisgemi</span>
                                        <span className="block text-xs text-gray-500">Admin Panel</span>
                                    </div>
                                )}
                            </Link>
                        </div>

                        {/* Navigation */}
                        <div className="mt-8 flex-grow flex flex-col">
                            <nav className="flex-1 px-2 space-y-1">
                                {sidebarItems.map((item) => {
                                    const Icon = item.icon;
                                    const active = isActive(item.href);
                                    return (
                                        <Link
                                            key={item.name}
                                            to={item.href}
                                            title={sidebarCollapsed ? item.name : undefined}
                                            className={`${active
                                                ? 'bg-primary-50 border-r-2 border-primary-500 text-primary-700'
                                                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                                                } group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors relative`}
                                        >
                                            <Icon
                                                className={`${active ? 'text-primary-500' : 'text-gray-400 group-hover:text-gray-500'
                                                    } mr-3 flex-shrink-0 h-5 w-5 transition-colors`}
                                            />
                                            {!sidebarCollapsed && <span className="flex-1">{item.name}</span>}
                                            {!sidebarCollapsed && item.badge && (
                                                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                                    {item.badge}
                                                </span>
                                            )}
                                        </Link>
                                    );
                                })}
                            </nav>

                            {/* User section */}
                            <div className="flex-shrink-0 flex border-t border-gray-200 p-4">
                                <div className={`flex items-center ${sidebarCollapsed ? 'justify-center w-full' : ''}`}>
                                    <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                                        <User className="w-5 h-5 text-primary-600" />
                                    </div>
                                    {!sidebarCollapsed && (
                                        <div className="ml-3">
                                            <p className="text-sm font-medium text-gray-700">
                                                {user?.first_name} {user?.last_name}
                                            </p>
                                            <p className="text-xs text-gray-500">Administrador</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Mobile sidebar */}
            {sidebarOpen && (
                <div className="fixed inset-0 flex z-40 lg:hidden">
                    <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
                    <div className="relative flex-1 flex flex-col max-w-xs w-full bg-white">
                        <div className="absolute top-0 right-0 -mr-12 pt-2">
                            <button
                                className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                                onClick={() => setSidebarOpen(false)}
                            >
                                <X className="h-6 w-6 text-white" />
                            </button>
                        </div>

                        <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
                            <div className="flex-shrink-0 flex items-center px-4">
                                <Link to="/" className="flex items-center space-x-2">
                                    <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center">
                                        <span className="text-white font-bold text-lg">T</span>
                                    </div>
                                    <div>
                                        <span className="text-xl font-bold text-gray-900">Eisgemi</span>
                                        <span className="block text-xs text-gray-500">Admin Panel</span>
                                    </div>
                                </Link>
                            </div>

                            <nav className="mt-8 flex-1 px-2 space-y-1">
                                {sidebarItems.map((item) => {
                                    const Icon = item.icon;
                                    const active = isActive(item.href);

                                    return (
                                        <Link
                                            key={item.name}
                                            to={item.href}
                                            onClick={() => setSidebarOpen(false)}
                                            className={`${active
                                                ? 'bg-primary-50 border-r-2 border-primary-500 text-primary-700'
                                                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                                                } group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors`}
                                        >
                                            <Icon
                                                className={`${active ? 'text-primary-500' : 'text-gray-400 group-hover:text-gray-500'
                                                    } mr-3 flex-shrink-0 h-5 w-5 transition-colors`}
                                            />
                                            <span className="flex-1">{item.name}</span>
                                            {item.badge && (
                                                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                                    {item.badge}
                                                </span>
                                            )}
                                        </Link>
                                    );
                                })}
                            </nav>
                        </div>
                    </div>
                </div>
            )}

            {/* Main content */}
            <div className="flex flex-col w-0 flex-1 overflow-hidden">
                {/* Top header */}
                <div className="relative z-10 flex-shrink-0 flex h-16 bg-white shadow border-b border-gray-200">
                    {/* Mobile menu button */}
                    <button
                        className="px-4 border-r border-gray-200 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 lg:hidden"
                        onClick={() => setSidebarOpen(true)}
                    >
                        <Menu className="h-6 w-6" />
                    </button>

                    {/* Header content */}
                    <div className="flex-1 px-4 flex justify-between items-center">
                        <div className="flex items-center gap-2">
                            {/* Desktop collapse toggle */}
                            <button
                                onClick={toggleSidebarCollapsed}
                                className="hidden lg:inline-flex items-center justify-center h-9 w-9 rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50"
                                title={sidebarCollapsed ? 'Expandir menú' : 'Colapsar menú'}
                            >
                                {sidebarCollapsed ? <ChevronsRight className="h-5 w-5" /> : <ChevronsLeft className="h-5 w-5" />}
                            </button>
                            <h1 className="text-lg font-medium text-gray-900">
                                {['/admin', '/admin/'].includes(location.pathname) ? 'Admin Panel' : ''}
                            </h1>
                        </div>

                        <div className="flex items-center space-x-4">
                            {/* Notifications */}
                            <button className="relative p-2 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded-lg">
                                <Bell className="h-5 w-5" />
                                <span className="absolute top-1 right-1 block h-2 w-2 rounded-full bg-red-400"></span>
                            </button>

                            {/* Ver sitio web */}
                            <Link
                                to="/"
                                className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                            >
                                Ver Sitio
                            </Link>

                            {/* User menu */}
                            <div className="relative">
                                <button
                                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                                    className="flex items-center space-x-2 p-2 text-sm rounded-lg text-gray-700 hover:bg-gray-100"
                                >
                                    <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                                        <User className="w-5 h-5 text-primary-600" />
                                    </div>
                                    <ChevronDown className="w-4 h-4" />
                                </button>

                                {userMenuOpen && (
                                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
                                        <div className="px-4 py-2 border-b border-gray-100">
                                            <p className="text-sm font-medium text-gray-900">
                                                {user?.first_name} {user?.last_name}
                                            </p>
                                            <p className="text-xs text-gray-500">Administrador</p>
                                        </div>

                                        <Link
                                            to="/profile"
                                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                            onClick={() => setUserMenuOpen(false)}
                                        >
                                            Mi Perfil
                                        </Link>

                                        <button
                                            onClick={() => {
                                                setUserMenuOpen(false);
                                                handleLogout();
                                            }}
                                            className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                                        >
                                            <div className="flex items-center">
                                                <LogOut className="w-4 h-4 mr-2" />
                                                Cerrar Sesión
                                            </div>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Page content */}
                <main className="flex-1 relative overflow-y-auto focus:outline-none bg-gray-50">
                    <Outlet />
                </main>
            </div>

            {/* Toast notifications */}
            <Toaster
                position="top-right"
                toastOptions={{
                    duration: 4000,
                    style: { background: '#363636', color: '#fff' },
                    success: { duration: 3000, iconTheme: { primary: '#10B981', secondary: '#fff' } },
                    error: { duration: 5000, iconTheme: { primary: '#EF4444', secondary: '#fff' } },
                }}
            />
        </div>
    );
};

export default AdminLayout;
